import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Surface, Text, useTheme } from 'react-native-paper';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { verifyMagicLink, getUserDetails } from '@/src/api/apiService';
import { useAuth } from '@/src/context/AuthContext';
import { getOnboardingRedirectForLogin } from '@/src/utils/onboardingRedirect';
import {
  clearOwnerDriverStorage,
  clearOwnerDriverStorageIfAccountChanged,
} from '@/src/features/owner/ownerDriverPrompt';

// Magic-link auth screen.
//
// Deep-link: alldrivers://auth/magic-link?token=...
// Web static route: /auth/magic-link?token=...
//
// Exchanges a single-use short-TTL token for a session key via verifyMagicLink,
// persists the key via authenticateWithToken, then redirects through
// getOnboardingRedirectForLogin (same logic as the email/password login flow).

type ScreenState = 'loading' | 'success' | 'error';

const normalizeParam = (value: string | string[] | undefined): string => {
  if (Array.isArray(value)) {
    return value[0] || '';
  }
  return value || '';
};

export default function MagicLinkScreen() {
  const theme = useTheme();
  const router = useRouter();
  const params = useLocalSearchParams<{ token?: string | string[] }>();
  const token = useMemo(() => normalizeParam(params.token).trim(), [params.token]);
  const { authenticateWithToken } = useAuth();

  const [state, setState] = useState<ScreenState>(token ? 'loading' : 'error');

  // Holds the in-flight/settled exchange promise for the current token.
  // Shared across StrictMode effect re-entries so the token is only exchanged once.
  const exchangeRef = useRef<{ token: string; promise: Promise<string> } | null>(null);
  // Guards against double navigation when the effect re-runs before the promise settles.
  const navDoneRef = useRef(false);

  useEffect(() => {
    if (!token) {
      exchangeRef.current = null;
      navDoneRef.current = false;
      setState('error');
      return;
    }

    let cancelled = false;

    if (!exchangeRef.current || exchangeRef.current.token !== token) {
      navDoneRef.current = false;
      setState('loading');
      const promise = (async (): Promise<string> => {
        const verifyRes = await verifyMagicLink(token);
        if (!verifyRes.ok || !verifyRes.data?.key?.trim()) {
          throw new Error('verify_failed');
        }
        const key = verifyRes.data.key.trim();
        await authenticateWithToken(key);

        let route = '/dashboard/home';
        try {
          const detailsRes = await getUserDetails();
          if (detailsRes.ok && detailsRes.data) {
            const accountEmail = (detailsRes.data as { email?: string }).email;
            if (accountEmail) {
              await clearOwnerDriverStorageIfAccountChanged(accountEmail);
            } else {
              await clearOwnerDriverStorage();
            }
            const redirect = await getOnboardingRedirectForLogin(detailsRes.data);
            if (redirect.shouldRedirect && redirect.route) {
              route = redirect.route;
            }
          } else {
            await clearOwnerDriverStorage();
            // Token already stored, but without account details fail closed for
            // owner intent before falling back to the dashboard.
          }
        } catch {
          await clearOwnerDriverStorage();
          // Network/parse failure after valid token stored; fail closed for
          // owner intent so a previous account cannot influence this session.
        }
        return route;
      })();
      exchangeRef.current = { token, promise };
    }

    exchangeRef.current.promise.then(
      (route) => {
        if (cancelled) return;
        setState('success');
        if (!navDoneRef.current) {
          navDoneRef.current = true;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          router.replace(route as any);
        }
      },
      () => {
        if (cancelled) return;
        setState('error');
      },
    );

    return () => {
      cancelled = true;
    };
  }, [token, authenticateWithToken, router]);

  const title =
    state === 'loading'
      ? 'A validar o link de acesso'
      : state === 'success'
      ? 'Acesso confirmado'
      : 'Não foi possível entrar';

  const statusMessage =
    state === 'loading'
      ? 'Por favor aguarda enquanto confirmamos o teu acesso.'
      : state === 'success'
      ? 'A redirecionar…'
      : 'Ocorreu um problema ao validar o teu acesso.';

  const errorDetail = !token
    ? 'O link de acesso não incluía um código válido.'
    : 'Não foi possível validar o link de acesso. Por favor solicita um novo link.';

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.center}>
        <Surface style={styles.content} elevation={1}>
          {state === 'loading' && (
            <ActivityIndicator
              testID="magic-link-loading"
              size="large"
              color={theme.colors.primary}
            />
          )}
          <Text testID="magic-link-title" variant="headlineSmall" style={styles.title}>
            {title}
          </Text>
          <Text testID="magic-link-message" variant="bodyMedium" style={styles.description}>
            {statusMessage}
          </Text>
          {state === 'error' && (
            <Text
              testID="magic-link-error"
              variant="bodySmall"
              style={[styles.description, { color: theme.colors.error }]}
            >
              {errorDetail}
            </Text>
          )}
        </Surface>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    padding: 16,
  },
  content: {
    borderRadius: 8,
    padding: 20,
  },
  title: {
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  description: {
    marginBottom: 14,
    opacity: 0.8,
    textAlign: 'center',
  },
});
